import { API_BASE_URL, AuthApiClient as AuthApiClientType } from './AuthApiClient'
import {
  ACCOUNT_BANNED_MESSAGE,
  AUTH_BANNED_EVENT,
  type AuthUser,
  type RefreshResult,
} from './types'
import { classifyRefreshFailure } from './utils'

const USER_STORAGE_KEY = 'xrift-user'

class AuthTokenManagerClass {
  private cachedUser: AuthUser | null = null
  private refreshPromise: Promise<RefreshResult> | null = null
  private refreshCleanupTimer: ReturnType<typeof setTimeout> | null = null
  private apiClient: typeof AuthApiClientType | null = null

  constructor() {
    const userJson = localStorage.getItem(USER_STORAGE_KEY)
    if (userJson) {
      try {
        this.cachedUser = JSON.parse(userJson)
      } catch {
        this.cachedUser = null
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageChange)
    }
  }

  init(apiClient: typeof AuthApiClientType): void {
    this.apiClient = apiClient
  }

  private handleStorageChange = (event: StorageEvent): void => {
    if (event.key === USER_STORAGE_KEY) {
      if (event.newValue) {
        try {
          this.cachedUser = JSON.parse(event.newValue)
        } catch {
          this.cachedUser = null
        }
      } else {
        this.cachedUser = null
      }
    }
  }

  setUser(user: AuthUser): void {
    this.cachedUser = user
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
  }

  clearUser(): void {
    this.cachedUser = null
    localStorage.removeItem(USER_STORAGE_KEY)
    this.refreshPromise = null
    if (this.refreshCleanupTimer) {
      clearTimeout(this.refreshCleanupTimer)
      this.refreshCleanupTimer = null
    }
  }

  getCachedUser(): AuthUser | null {
    return this.cachedUser
  }

  isAuthenticated(): boolean {
    return Boolean(this.cachedUser)
  }

  async refreshAccessToken(): Promise<RefreshResult> {
    // リフレッシュは「ログイン中のはず」の時だけ叩く。
    // キャッシュユーザーが無い（ゲスト・ログアウト済み）クライアントが 401 や WebSocket の
    // 再接続で叩いても、Cookie は無いので 401 REFRESH_TOKEN_MISSING が返るだけ。
    // それが「ログイン中なのに Cookie が消えた」事例の観測ログ（xrift-backend #603）を
    // ノイズで埋めていたので、ネットワークに出さずに「セッション無し」として返す。
    // Cookie だけ残った端末の復帰は AuthContext の起動時プローブ（/auth/me）が担う。
    if (!this.isAuthenticated()) return 'no-session'
    if (this.refreshPromise) return this.refreshPromise

    this.refreshPromise = this.executeRefresh()
      .then((result) => {
        // 成功時のみ結果を一定時間キャッシュ（重複リフレッシュ抑止）。
        // 失敗時は即座にクリアし、次回の呼び出しで再試行できるようにする。
        if (result === 'refreshed') {
          this.refreshCleanupTimer = setTimeout(() => {
            this.refreshPromise = null
            this.refreshCleanupTimer = null
          }, 10_000)
        } else {
          this.refreshPromise = null
        }
        return result
      })
      .catch((error) => {
        this.refreshPromise = null
        throw error
      })

    return this.refreshPromise
  }

  private async executeRefresh(retryCount = 0): Promise<RefreshResult> {
    const MAX_RETRIES = 2
    const RETRY_DELAY_MS = 1000

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      const csrfToken = this.apiClient?.getCsrfToken() ?? null
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers,
        credentials: 'include',
      })

      if (!response.ok) {
        const result = classifyRefreshFailure(response.status, await this.readErrorCode(response))

        if (result === 'banned') {
          console.error(ACCOUNT_BANNED_MESSAGE)
          this.clearUser()
          window.dispatchEvent(new CustomEvent(AUTH_BANNED_EVENT))
          return result
        }

        // 確定的な認証失効。この時だけ認証状態をクリアする。
        if (result === 'unauthorized') {
          console.error(`セッションが無効です (${response.status})。再ログインが必要です。`)
          this.clearUser()
          return result
        }

        // 即リトライするのは 5xx だけ。429 は叩き直すと制限解除を遅らせるので呼び出し側の再試行に任せる
        if (response.status >= 500 && retryCount < MAX_RETRIES) {
          console.error(
            `リフレッシュAPIサーバーエラー (${response.status})、リトライします... (${retryCount + 1}/${MAX_RETRIES})`,
          )
          await this.delay(RETRY_DELAY_MS)
          return this.executeRefresh(retryCount + 1)
        }

        // 一過性エラー扱い。認証状態は保持する。
        console.error(`リフレッシュAPI一時エラー: ${response.status}`)
        return result
      }

      const data = await response.json()
      if (data.user) {
        this.setUser(data.user)
      }

      return 'refreshed'
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        console.error(
          `リフレッシュネットワークエラー、リトライします... (${retryCount + 1}/${MAX_RETRIES})`,
          error,
        )
        await this.delay(RETRY_DELAY_MS)
        return this.executeRefresh(retryCount + 1)
      }

      // ネットワークエラーも一過性扱い。認証状態は保持し、ネットワーク復旧後に再試行する。
      console.error('リフレッシュ失敗（リトライ上限）。ネットワーク復旧後に再試行されます。', error)
      return 'transient-error'
    }
  }

  /** 失敗レスポンスの body から `code` を読む。body が無い・JSON でない場合は undefined */
  private async readErrorCode(response: Response): Promise<string | undefined> {
    try {
      const data = await response.json()
      return typeof data?.code === 'string' ? data.code : undefined
    } catch {
      return undefined
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  async migrateSession(): Promise<boolean> {
    const oldToken = localStorage.getItem('xrift-token')
    if (!oldToken) return false

    try {
      const response = await fetch(`${API_BASE_URL}/auth/migrate-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${oldToken}`,
        },
        credentials: 'include',
      })
      localStorage.removeItem('xrift-token')
      localStorage.removeItem('xrift-refresh-token')
      return response.ok
    } catch {
      return false
    }
  }
}

export const AuthTokenManager = new AuthTokenManagerClass()
